import React, { useState } from 'react';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { FolderTree, Palette, Filter, X } from 'lucide-react';

/* Introducción a las carpetas — se ve UNA vez por persona.
   La marca va en el usuario (`visto.carpetas`, en el servidor) y no en el
   navegador: "una sola vez" tiene que significar una por persona, no una por
   cada equipo desde el que entra. Si la petición falla se guarda igual en local,
   porque enseñar dos veces el mismo tutorial es peor que no registrarlo. */

const PASOS = [
  {
    icono: FolderTree,
    titulo: 'Tus carpetas, en tu buzón',
    texto: 'Crea carpetas desde la barra lateral para guardar el correo por tema: un cliente, las facturas, un proyecto. No son etiquetas de este portal: son carpetas de verdad, así que las verás igual en el móvil o en cualquier otro programa de correo.',
  },
  {
    icono: Palette,
    titulo: 'Con subcarpetas y color',
    texto: 'Dentro de una carpeta puedes crear otras —Clientes → 2026 → Contratos— y darle a cada una un color para reconocerla de un vistazo. Renombrar una carpeta no pierde nada, y al eliminarla el correo que tuviera dentro se pone a salvo antes.',
  },
  {
    icono: Filter,
    titulo: 'Que el correo entre solo',
    texto: 'En Configuración → Reglas puedes decir qué correo va a cada carpeta: por remitente, por destinatario (Para o Cc) o por palabras dentro del mensaje. Funciona en el servidor, así que el correo se ordena aunque tengas esto cerrado.',
  },
];

export default function IntroCarpetas() {
  const { user, setUser } = useStore();
  const [paso, setPaso] = useState(0);
  const yaVisto = !!user?.visto?.carpetas;
  const [cerrado, setCerrado] = useState(yaVisto);

  if (cerrado || yaVisto || !user) return null;

  const cerrar = async (crear = false) => {
    setCerrado(true);
    setUser({ ...user, visto: { ...(user.visto || {}), carpetas: new Date().toISOString() } });
    try { await api.post('/me/visto', { clave: 'carpetas' }); } catch (_) {}
    // La barra lateral escucha esto y abre el diálogo de crear carpeta: acabar
    // el tutorial con "ya, ¿y dónde se hace?" no sirve de nada.
    if (crear) window.dispatchEvent(new CustomEvent('orvian:nueva-carpeta'));
  };

  const P = PASOS[paso];
  const Icono = P.icono;
  const ultimo = paso === PASOS.length - 1;

  return (
    <Dialog open onOpenChange={(o) => !o && cerrar(false)}>
      <DialogContent className="sm:max-w-[460px] p-0 overflow-hidden" data-testid="intro-carpetas">
        <div className="p-6 pb-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div
              className="h-11 w-11 rounded-xl grid place-items-center shrink-0"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
            >
              <Icono className="h-5 w-5" strokeWidth={1.9} />
            </div>
            <button
              type="button"
              onClick={() => cerrar(false)}
              className="od-icon-btn-sm shrink-0"
              aria-label="Cerrar"
              data-testid="intro-carpetas-cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <h2 className="text-lg font-semibold mb-2">{P.titulo}</h2>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{P.texto}</p>
        </div>

        <div
          className="flex items-center justify-between gap-3 px-6 py-4"
          style={{ borderTop: '1px solid var(--line)' }}
        >
          <div className="flex items-center gap-1.5">
            {PASOS.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === paso ? 18 : 6, height: 6, borderRadius: 999,
                  background: i === paso ? 'var(--accent)' : 'var(--line)',
                  transition: 'width .18s ease',
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!ultimo && (
              <Button variant="ghost" size="sm" onClick={() => cerrar(false)} data-testid="intro-carpetas-saltar">
                Saltar
              </Button>
            )}
            {ultimo ? (
              <Button size="sm" onClick={() => cerrar(true)} data-testid="intro-carpetas-crear">
                Crear mi primera carpeta
              </Button>
            ) : (
              <Button size="sm" onClick={() => setPaso((p) => p + 1)} data-testid="intro-carpetas-siguiente">
                Siguiente
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
